const API_BASE = '/api';

function getToken() {
  return localStorage.getItem('ads_token');
}

function setToken(token) {
  localStorage.setItem('ads_token', token);
}

function clearToken() {
  localStorage.removeItem('ads_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };

  // Don't force JSON content-type when sending FormData (file uploads) —
  // the browser needs to set its own multipart boundary.
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

export const api = {
  // --------------------------------------------------
  // Authentication
  // --------------------------------------------------

  login: (email, password) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  // --------------------------------------------------
  // Existing Ads / Keyword Intelligence
  // --------------------------------------------------

  suggestKeywords: (payload) =>
    request('/ads/suggest-keywords', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // --------------------------------------------------
  // Clients
  // --------------------------------------------------

  getClients: () => request('/clients'),

  getClient: (id) =>
    request(`/clients/${id}`),

  createClient: (payload) =>
    request('/clients', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateClient: (id, payload) =>
    request(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  deleteClient: (id) =>
    request(`/clients/${id}`, {
      method: 'DELETE',
    }),

  // --------------------------------------------------
  // Ads
  // --------------------------------------------------

  toggleStatus: (id, action) =>
    request(`/ads/${id}/toggle-status`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  getAds: (clientId) =>
    request(clientId ? `/ads?client_id=${clientId}` : '/ads'),

  getAd: (id) =>
    request(`/ads/${id}`),

  runReport: (id) =>
    request(`/ads/${id}/run-report`, {
      method: 'POST',
    }),

  createAd: (formData) =>
    request('/ads', {
      method: 'POST',
      body: formData,
    }),

  updateAd: (id, formData) =>
    request(`/ads/${id}`, {
      method: 'PUT',
      body: formData,
    }),

  getDashboardStats: () =>
    request('/ads/stats/summary'),

  getOpenAIUsage: () =>
    request('/ads/openai/usage'),

  getOpenAIUsageDiagnostic: () =>
    request('/ads/openai/usage-diagnostic'),

  getOpenAIUsageSummary: () =>
    request('/ads/openai/usage-summary'),

  getOpenAIUsageGroupings: () =>
    request('/ads/openai/usage-groupings'),

  getOpenAIUsageOverview: () =>
    request('/ads/openai/usage-overview'),

  deleteAd: (id) =>
    request(`/ads/${id}`, {
      method: 'DELETE',
    }),

  // --------------------------------------------------
  // AI Creative Director
  // --------------------------------------------------

  creativeDirectorConcept: (payload) =>
    request('/creative-director/concept', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),


  finalizeCreativeConcept: (payload) =>
    request('/creatives/finalize', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),



  // --------------------------------------------------
  // AI Creative Generation
  // --------------------------------------------------

  generateCreative: (payload) =>
    request('/creatives/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

generateCreativeVariants: (payload) =>
  request('/creatives/variants', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

getCreativeVariantJob: (jobId) =>
  request(`/creatives/variants/${encodeURIComponent(jobId)}`),



  // --------------------------------------------------
  // Independent Greeting Creative Generation
  // --------------------------------------------------

  suggestGreetingText: (payload) =>
    request('/greetings/suggestions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  generateGreeting: (payload) =>
    request('/greetings/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  startGreetingVariants: (payload) =>
    request('/greetings/variants', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getGreetingVariants: (jobId) =>
    request(`/greetings/variants/${encodeURIComponent(jobId)}`),

  // --------------------------------------------------
  // Saved Creative Gallery
  // --------------------------------------------------

  getCreatives: () =>
    request('/creatives'),

  deleteCreative: (filename) =>
    request(`/creatives/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    }),

  // Shared Font Manager
  getFonts: () => request('/fonts'),

  getFontFile: (family) =>
    request(`/fonts/file?family=${encodeURIComponent(family)}`),

  refreshFonts: () =>
    request('/fonts/refresh', { method: 'POST' }),

  saveFontSettings: (disabledFamilies) =>
    request('/fonts/settings', {
      method: 'PUT',
      body: JSON.stringify({ disabledFamilies }),
    }),
};

export { getToken, setToken, clearToken };
